import { computed } from 'vue';
import { useHead } from '@unhead/vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { SUPPORTED_LOCALES, DEFAULT_LOCALE, type Locale } from '@/i18n/locales';
import { getSiteUrl } from '@/lib/config';

/**
 * Источник строки SEO-поля: либо статическая строка (читается единожды
 * при вызове `useSeo`), либо getter — тогда поле реактивно и пересчитывается
 * при смене router-параметров без remount страницы (например, на смене
 * `:slug` в `/:lang/solutions/:slug`).
 */
export type SeoStringSource = string | (() => string);

export interface UseSeoOptions {
  /** Основной заголовок (без суффикса « — Bafsy»). */
  readonly title?: SeoStringSource;
  /** Meta description (<=160 символов рекомендуется). */
  readonly description?: SeoStringSource;
  /** URL OG-картинки (абсолютный или relative к origin). */
  readonly ogImage?: string;
  /** Если true — ставит `<meta name="robots" content="noindex,nofollow">`. */
  readonly noindex?: boolean;
}

const BRAND_SUFFIX = 'Bafsy';

function resolveOrigin(): string {
  return getSiteUrl();
}

const OG_LOCALE_MAP: Record<Locale, string> = {
  ru: 'ru_RU',
  en: 'en_US',
};

function stripTrailingSlash(path: string): string {
  if (path.length > 1 && path.endsWith('/')) return path.slice(0, -1);
  return path;
}

function rewritePathForLocale(path: string, target: Locale): string {
  const match = path.match(/^\/(ru|en)(\/|$)/);
  if (match) {
    return `/${target}${path.slice(match[1].length + 1) || '/'}`;
  }
  return `/${target}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Регистрирует SEO-мета для страницы через `@unhead/vue`. Вызывается
 * в `<script setup>` любой страницы. Реактивен: при смене локали
 * и маршрута ссылки/hreflang пересчитаются.
 *
 * Поля:
 *   - title: `<title>` = `${title} — Bafsy`;
 *   - description: `<meta name="description">`;
 *   - OG: `og:title`, `og:description`, `og:image`, `og:type=website`;
 *   - Twitter: `twitter:card=summary_large_image`;
 *   - robots: `noindex,nofollow` при `noindex: true`;
 *   - canonical: `origin + route.path` + trailing slash (nginx отдаёт каталоги);
 *   - hreflang: ссылки на ru/en + x-default (=ru).
 */
function resolveCurrentLocale(path: string): Locale {
  const match = path.match(/^\/(ru|en)(\/|$)/);
  if (match && (SUPPORTED_LOCALES as readonly string[]).includes(match[1])) {
    return match[1] as Locale;
  }
  return DEFAULT_LOCALE;
}

function resolveSeoString(source: SeoStringSource | undefined): string {
  if (source === undefined) return '';
  if (typeof source === 'function') return source();
  return source;
}

export function useSeo(options: UseSeoOptions = {}): void {
  const route = useRoute();
  const { t } = useI18n();

  const meta = computed(() => {
    const title = resolveSeoString(options.title).trim();
    const fullTitle = title.length > 0 ? `${title} — ${BRAND_SUFFIX}` : t('seo.defaults.title');
    const resolvedDescription = resolveSeoString(options.description);
    const description =
      resolvedDescription.length > 0 ? resolvedDescription : t('seo.defaults.description');
    const origin = resolveOrigin();
    const path = stripTrailingSlash(route.path || '/');
    const withSlash = (value: string): string => (value.endsWith('/') ? value : `${value}/`);
    const canonical = `${origin}${withSlash(path || '/')}`;
    const currentLocale = resolveCurrentLocale(route.path || '/');

    const alternates = SUPPORTED_LOCALES.map((lang) => ({
      lang,
      href: `${origin}${withSlash(stripTrailingSlash(rewritePathForLocale(path || '/', lang)))}`,
    }));

    const xDefaultHref = `${origin}${withSlash(stripTrailingSlash(rewritePathForLocale(path || '/', DEFAULT_LOCALE)))}`;

    return {
      fullTitle,
      description,
      canonical,
      alternates,
      xDefaultHref,
      ogImage: options.ogImage ?? `${origin}/og-default.svg`,
      ogLocale: OG_LOCALE_MAP[currentLocale],
      ogAlternateLocales: SUPPORTED_LOCALES.filter((l) => l !== currentLocale).map(
        (l) => OG_LOCALE_MAP[l],
      ),
      noindex: options.noindex === true,
    };
  });

  useHead({
    title: () => meta.value.fullTitle,
    htmlAttrs: { lang: () => resolveCurrentLocale(route.path || '/') },
    meta: () => [
      { name: 'description', content: meta.value.description },
      { property: 'og:title', content: meta.value.fullTitle },
      { property: 'og:description', content: meta.value.description },
      { property: 'og:image', content: meta.value.ogImage },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: meta.value.canonical },
      { property: 'og:site_name', content: BRAND_SUFFIX },
      { property: 'og:locale', content: meta.value.ogLocale },
      ...meta.value.ogAlternateLocales.map((loc) => ({
        property: 'og:locale:alternate',
        content: loc,
      })),
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: meta.value.fullTitle },
      { name: 'twitter:description', content: meta.value.description },
      { name: 'twitter:image', content: meta.value.ogImage },
      ...(meta.value.noindex ? [{ name: 'robots', content: 'noindex,nofollow' }] : []),
    ],
    link: () => [
      { rel: 'canonical' as const, href: meta.value.canonical },
      ...meta.value.alternates.map((alt) => ({
        rel: 'alternate' as const,
        hreflang: alt.lang,
        href: alt.href,
      })),
      { rel: 'alternate' as const, hreflang: 'x-default', href: meta.value.xDefaultHref },
    ],
  });
}
